import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import {
  type ApplicationData,
  type ApplicationPageData,
  type ProgrammeChoice,
} from './types';
import { type PROGRAMME, type STUDY_MODE } from '../../components';
import { APPLICATION_STATUS_TYPE } from '../../../../utils/statusConstants/statusConstants';
import { type ApplicationDeclarationData } from '../../components/ApplicationForm/Declaration/types';

export const useGetApplicationCardListPageData = (
  email: string,
): ApplicationPageData[] => {
  const { data: applicationData } = useQuery<ApplicationData>(
    gql`
      query ($email: String) {
        applications(filter: { applicant: { email: { eq: $email } } }) {
          _id
          applicationNo
          programmeChoice {
            department
            studyMode
            programme
            entryYear
          }
          status
          declaration {
            personalType
            confirmation
            informationAccuracy
            engagement
          }
        }
      }
    `,
    {
      variables: { email },
      skip: !email,
    },
  );

  const applications = applicationData?.applications ?? [];

  return applications.map(application => {
    const {
      _id,
      applicationNo,
      status = '',
      programmeChoice = {} as ProgrammeChoice,
      declaration = {} as ApplicationDeclarationData,
    } = application ?? {};

    const {
      department = ' ' as string,
      studyMode = '' as STUDY_MODE,
      programme = '' as PROGRAMME,
      entryYear = '',
    } = programmeChoice;

    // Applicants may edit whole application form under these statuses,
    // thus no separate declaration entry / page needed.
    const editEnabledStatusList = [
      APPLICATION_STATUS_TYPE.CREATED,
      APPLICATION_STATUS_TYPE.RETURNED,
    ];

    // Applicants have no access to any parts of application under these statuses,
    // thus no declaration entry / page needed.
    const noAccessStatusList = [
      APPLICATION_STATUS_TYPE.WITHDRAWN,
      APPLICATION_STATUS_TYPE.OVERDUE,
      APPLICATION_STATUS_TYPE.DECLINED,
    ];

    const needsDeclaration =
      !editEnabledStatusList.includes(status as APPLICATION_STATUS_TYPE) &&
      !noAccessStatusList.includes(status as APPLICATION_STATUS_TYPE) &&
      declaration === null;

    return {
      id: _id,
      applicationNo,
      title: department,
      studyMode,
      programme,
      entryYear,
      status: status as APPLICATION_STATUS_TYPE,
      needsDeclaration,
    };
  });
};
